import { ChainName, validateChainName } from "@/constants/chains";
import { isMainnet } from "@/env";

export const CIRCLE_API_URL = isMainnet
  ? "https://iris-api.circle.com"
  : "https://iris-api-sandbox.circle.com";

export const CCTP_DOMAIN_IDS: Record<ChainName, number> = {
  Ethereum: 0,
  Arbitrum: 3,
  Solana: 5,
  Base: 6,
  Sui: 8,
  Aptos: 9,
  Sepolia: 0,
  ArbitrumSepolia: 3,
  BaseSepolia: 6,
};

export function getDomainId(chain: string): number | null {
  const chainName = validateChainName(chain);

  if (!chainName) return null;

  return CCTP_DOMAIN_IDS[chainName];
}

export function getChainNameFromDomainId(domainId: number): ChainName | null {
  const chainName = Object.entries(CCTP_DOMAIN_IDS).find(([, id]) => id === domainId)?.[0] ?? null;

  return validateChainName(chainName) ?? null;
}

/** EVM CCTP domain IDs — addresses are 20 bytes left-padded to 32 bytes */
const EVM_DOMAIN_IDS = new Set([0, 3, 6]); // Ethereum, Arbitrum, Base

/**
 * Extracts the destination address from a CCTP mintRecipient (32-byte hex).
 *
 * - EVM: strips 12-byte left-padding to return the 20-byte address
 * - Aptos / others: returns full 32-byte 0x-prefixed hex
 *
 * NOTE: For Solana (domain 5), the mintRecipient is the USDC Associated Token
 * Account (ATA), NOT the wallet pubkey. Callers should use the user's wallet
 * address directly for Solana destinations instead of this function.
 */
export function formatMintRecipientAddress(mintRecipient: string, destDomain: number): string {
  const hex = mintRecipient.replace(/^0x/, "");
  if (EVM_DOMAIN_IDS.has(destDomain)) {
    // EVM addresses are 20 bytes stored in the rightmost bytes of the 32-byte field
    return `0x${hex.slice(-40)}`;
  }
  return `0x${hex}`;
}

/**
 * Validates that an attestation is a valid hex string, not a status placeholder.
 * Circle API returns "PENDING" as attestation value when not yet ready.
 */
export function isValidHexAttestation(
  attestation: string | undefined | null,
): attestation is string {
  if (!attestation || attestation === "PENDING") return false;
  const hexPart = attestation.startsWith("0x") ? attestation.slice(2) : attestation;
  return hexPart.length > 0 && /^[0-9a-fA-F]+$/.test(hexPart);
}
